<template>
	<footer
		class="footer js-footer"
		:class="{
			'is-hidden-on-event': !$arenaConfig.showFooterOnEvent,
		}"
	>
		<div class="footer__logo-w">
			<div
				v-if="$arenaConfig.showPoweredBy"
				class="footer__logo-text"
			>
				powered by
			</div>
			<img
				v-if="$arenaConfig.footerImageUrl"
				class="footer__logo"
				:alt="$arenaConfig.browserTitle || 'Arena'"
				:src="$arenaConfig.footerImageUrl"
			>
			<a
				v-else
				href="https://asport.tv/"
				target="_blank"
			>
				<img
					class="footer__logo"
					alt="Asport"
					:src="$arenaConfig.isLightTheme ? '/images/asport_logo_dark.svg' : '/images/asport_logo_light.svg'"
				>
			</a>
		</div>

		<div class="footer__nav-w">
			<ul class="footer__nav">
				<li
					v-for="(item, i) in menu"
					:key="i"
					class="footer__nav-item"
				>
					<component
						:is="item.link ? 'a' : (item.action || item.emit ? 'button' : 'span')"
						:class="['footer__link', item.className, {'is-active': item.isActive }]"
						:href="item.link ? item.link : null"
						:type="!item.link && (item.action || item.emit) ? 'button' : null"
						:rel="item.rel || null"
						:title="item.title || null"
						:aria-current="item.isActive ? 'page' : null"
						@click="handleClick(item, $event)"
					>
						{{ item.text }}
					</component>
				</li>
				<li
					v-if="$arenaConfig.locales.length > 1"
					class="footer__nav-item"
				>
					<LangSwitcher
						:languages="$arenaConfig.locales"
						:current-language="$arenaConfig.currentLanguage"
						class="footer__link"
					/>
				</li>
			</ul>

			<div
				v-if="$arenaConfig.socialMedia.length"
				class="footer__social"
			>
				<a
					v-for="link in $arenaConfig.socialMedia"
					v-show="getSocialLinkIcon(link)"
					:key="link"
					:href="link"
					rel="nooponer"
					target="blank"
					class="footer__social-link"
					:aria-label="getSocialLinkLabel(link)"
				>
					<V2Icon
						v-if="getSocialLinkIcon(link)"
						:icon-name="getSocialLinkIcon(link)"
						size="lg"
					/>
				</a>
			</div>
		</div>
	</footer>
</template>

<script>
import LangSwitcher from '@js/components/LangSwitcher';
import V2Icon from '@shared/components/Icon';
import laroute from '@laroute';

export default {
	name:       'Footer',
	components: { V2Icon, LangSwitcher },
	props:      {
		showCookiesMenu: {
			type:    Boolean,
			default: false,
		},
	},
	data() {
		return {
			laroute,
		};
	},
	computed: {
		menu() {
			const customImprintLink = this.getCustomImprintExternalLink();
			const customTermsLink = this.getCustomTermsExternalLink();

			const menu = [
				{
					text:     $I18n.trans('commons.contact'),
					emit:     'modal:ContactForm',
					isActive: false,
				},
				{
					text:      $I18n.trans('commons.imprint'),
					routeName: 'arena.imprint',
					link:      customImprintLink || laroute.route('arena.imprint'),
					isActive:  !customImprintLink && window.location.pathname === laroute.route('arena.imprint'),
				},
				{
					text:      this.getCustomTermsLabel() || $I18n.trans('commons.terms'),
					routeName: 'arena.terms',
					link:      customTermsLink || laroute.route('arena.terms'),
					isActive:  !customTermsLink && window.location.pathname === laroute.route('arena.terms'),
				},
				{
					text:      $I18n.trans('arena.device_support.title'),
					routeName: 'arena.device_support',
					link:      laroute.route('arena.device_support'),
					isActive:  window.location.pathname === laroute.route('arena.device_support'),
				},
			];

			if (this.showCookiesMenu) {
				menu.push(
					{
						text:      $I18n.trans('arena.sportpass.cookies'),
						routeName: 'arena.cookies',
						link:      laroute.route('arena.cookies'),
						isActive:  window.location.pathname === laroute.route('arena.cookies'),
					}
				);
			}

			if (this.$arenaConfig.about) {
				menu.splice(1, 0, {
					text:      $I18n.trans('commons.about', { name: 'Sportpass' }),
					routeName: 'arena.about',
					link:      laroute.route('arena.about'),
					isActive:  window.location.pathname === laroute.route('arena.about'),
				});
			}

			if (this.$arenaConfig.isMz) {
				menu.splice(Math.max(menu.length - 1, 0), 0, {
					text:      'Cookie-Einstellungen',
					rel:       'Cookie-Einstellungen für diese Webseite',
					title:     'Cookie-Einstellungen für diese Webseite',
					className: 'nav-link',
					action:    this.showCmpCookieSettings,
					isActive:  false,
				});
			}

			return menu.filter(item => !this.isRouteDisabled(item.routeName));
		},
	},
	methods: {
		isRouteDisabled(routeName) {
			return Boolean(routeName) && (this.$arenaConfig.disabledRouteNames || []).includes(routeName);
		},
		getCustomImprintExternalLink() {
			const overrideMode = (this.$arenaConfig?.imprintOverrideMode || 'none').toString().toLowerCase();
			if (
				!this.$arenaConfig?.isCustomDomain ||
				overrideMode !== 'external_link'
			) {
				return null;
			}

			const translatedLink = this.$Helpers.getTranslatedContent(this.$arenaConfig?.imprintOverrideExternalLinks);
			if (!translatedLink) {
				return null;
			}

			return /^https?:\/\//i.test(translatedLink) ? translatedLink : null;
		},
		getCustomTermsExternalLink() {
			if (
				!this.$arenaConfig?.isCustomDomain ||
				!this.$arenaConfig?.termsOverrideEnabled
			) {
				return null;
			}

			const translatedLink = this.$Helpers.getTranslatedContent(this.$arenaConfig?.termsOverrideExternalLink);
			if (!translatedLink) {
				return null;
			}

			return /^https?:\/\//i.test(translatedLink) ? translatedLink : null;
		},
		getCustomTermsLabel() {
			if (!this.$arenaConfig?.termsOverrideEnabled) {
				return null;
			}

			const translatedLabel = this.$Helpers.getTranslatedContent(this.$arenaConfig?.termsOverrideLabel);
			return translatedLabel?.trim() || null;
		},
		getSocialLinkIcon(link) {
			switch (true) {
			case link.includes('instagram'):
				return 'logo-instagram-filled';
			case link.includes('facebook'):
				return 'logo-facebook-circle';
			case link.includes('twitter'):
				return 'logo-x';
			case link.includes('tiktok'):
				return 'logo-tiktok';
			case link.includes('youtube'):
				return 'logo-youtube-filled';
			case link.includes('linkedin'):
				return 'logo-linkedin-filled';
			case link.includes('flickr'):
				return 'logo-flickr-filled';
			default:
				return null;
			}
		},
		getSocialLinkLabel(link) {
			switch (true) {
			case link.includes('instagram'):
				return 'Instagram';
			case link.includes('facebook'):
				return 'Facebook';
			case link.includes('twitter'):
				return 'X';
			case link.includes('tiktok'):
				return 'TikTok';
			case link.includes('youtube'):
				return 'YouTube';
			case link.includes('linkedin'):
				return 'LinkedIn';
			case link.includes('flickr'):
				return 'Flickr';
			default:
				return link;
			}
		},
		showCmpCookieSettings() {
			if (typeof window.__cmp === 'function') {
				window.__cmp('showScreen');
			}
		},
		handleClick(item, event) {
			if (item.action) {
				event.preventDefault();
				item.action();
			}

			if (item.emit) {
				this.eventBus.$emit(item.emit);
			}
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@sass/variables';

.footer {
	display: flex;
	flex-wrap: wrap;
	justify-content: center;
	flex-direction: column;
	align-items: flex-start;
	background-color: var(--color-footer-background);
	padding-right: $grid-gutter-width;
	padding-left: $grid-gutter-width;
	width: 100%;
	// the follwing styles are required due to SDX conflicts
	z-index: 1;
	overflow: initial;

	body.-t-light & { // stylelint-disable-line selector-max-type, selector-no-qualifying-type
		border-top: 1px solid var(--color-grey-900);
	}

	/* stylelint-disable */
	body.-t-phygital &,
	body.-t-astana2026 &,
	body.-t-phygital-plus & {
		@include size(md-up) {
			border-top: 1px solid var(--color-grey-900);
		}
	}
	/* stylelint-enable */

	&.is-hidden-on-event {
		body.is-player-mode & { // stylelint-disable-line selector-max-type, selector-no-qualifying-type
			display: none;
		}
	}

	@include size(md-up) {
		justify-content: space-between;
		flex-direction: row;
		align-items: center;
	}
}

.footer__logo-w {
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	align-items: center;

	@include size(sm-down) {
		border-bottom: 1px solid var(--color-grey-800);
		width: 100%;
		text-align: center;
		padding-top: $sp4;
		padding-bottom: $sp4;
	}

	@include size(md-up) {
		padding-top: $sp3;
		align-items: flex-start;
	}
}

.footer__logo-text {
	@include font(primary, book, normal, fs-80);
	color: var(--color-grey-200);
	margin-bottom: $sp3;

	body.-t-light & { // stylelint-disable-line selector-max-type, selector-no-qualifying-type
		color: var(--color-black);
	}

	body.-st-stv-fsg & { // stylelint-disable-line selector-max-type, selector-no-qualifying-type
		color: var(--color-white);
	}
}

.footer__logo {
	height: 25px;
	width: 100%;
	object-fit: contain;

	&[src$='sport-austria-finals.png'] {
		height: 46px;
	}

	body.-st-stv-fsg & { // stylelint-disable-line selector-max-type, selector-no-qualifying-type
		filter: brightness(0) invert(1);
	}

	body.-st-euf & { // stylelint-disable-line selector-max-type, selector-no-qualifying-type
		height: 32px;
	}

	@include size(md-up) {
		width: auto;
		justify-content: space-between;
		margin-bottom: 0;

		body.-t-oeoc & { // stylelint-disable-line selector-max-type, selector-no-qualifying-type
			transform: scale(1.8);
		}

		body.-st-euf & { // stylelint-disable-line selector-max-type, selector-no-qualifying-type
			height: 48px;
		}
	}
}

.footer__nav-w {
	flex-grow: 1;
	align-self: center;
	padding-top: $sp3;
	padding-bottom: $sp3;

	@include size(md-up) {
		padding-top: $sp5;
		padding-bottom: $sp5;
	}
}

.footer__nav {
	display: flex;
	flex-wrap: wrap;
	flex-grow: 1;
	list-style: none;
	justify-content: flex-end;
	gap: $sp3;

	@include size(md-up) {
		gap: $sp3;
	}

	@include size(lg-up) {
		gap: $sp5;
	}
}

.footer__nav-item {
	display: block;
	width: 100%;
	text-align: center;

	@include size(md-up) {
		width: auto;
		text-align: left;
	}
}

.footer__link {
	display: inline-block;
	// the following !important's are required due to SDX conflicts
	color: var(--color-grey-200) !important; // stylelint-disable-line declaration-no-important
	padding-top: $sp3;
	padding-bottom: $sp3;
	padding-left: $sp4;
	padding-right: $sp4;
	cursor: pointer;

	body.-t-light & { // stylelint-disable-line selector-max-type, selector-no-qualifying-type
		color: var(--color-black) !important; // stylelint-disable-line declaration-no-important
	}

	.footer__nav-item:last-child & {
		padding-right: 0;
	}

	body.-st-stv-fsg & { // stylelint-disable-line selector-max-type, selector-no-qualifying-type
		color: var(--color-white) !important; // stylelint-disable-line declaration-no-important
	}

	&.is-active,
	&:hover {
		text-decoration: underline;
	}
}

.footer__social {
	display: flex;
	justify-content: center;
	gap: $sp5;
	margin-top: $sp5;

	@include size(md-up) {
		justify-content: flex-end;
		margin-top: $sp3;
	}
}

.footer__social-link {
	padding: $sp3;
	color: var(--color-grey-100) !important; // stylelint-disable-line declaration-no-important

	body.-st-stv-fsg & { // stylelint-disable-line selector-max-type, selector-no-qualifying-type
		color: var(--color-white) !important; // stylelint-disable-line declaration-no-important
	}

	&:hover {
		color: var(--color-hype-yellow) !important; // stylelint-disable-line declaration-no-important

		body.-st-stv-fsg & { // stylelint-disable-line selector-max-type, selector-no-qualifying-type
			color: var(--color-grey-900) !important; // stylelint-disable-line declaration-no-important
		}
	}
}

</style>
